import styled from "@emotion/styled";
import { differenceInCalendarDays, format, isValid, parseISO, subDays } from "date-fns";
import { ArrowLeft, ArrowUpDown, CalendarDays, Check, ChevronDown, ChevronLeft, ChevronRight, ListTree, RefreshCw, Search } from "lucide-react";
import { useState } from "react";
import type { DateRange } from "react-day-picker";
import { Link, useSearchParams } from "react-router-dom";
import { Spinner } from "@/components/ui/spinner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { computeQueuePeriodRange } from "@/helpers/underwritingDashboard";
import { getQueueHistoryRows, QUEUE_HISTORY_EVENT_LABELS } from "@/helpers/underwritingQueueHistory";
import type { QueueHistoryDirection, QueueHistoryEventType, QueueHistorySort } from "@/helpers/underwritingQueueHistory";
import type { QueueCustomRange, QueuePeriod } from "@/models/underwriting";
import { useApi } from "@/hooks/useApi";
import { underwritingService } from "@/services/UnderwritingService";

const PAGE_SIZE = 50;
const PERIOD_OPTIONS: { value: QueuePeriod; label: string }[] = [
  { value: "Today", label: "Today" },
  { value: "Last7Days", label: "Last 7 days" },
  { value: "Last30Days", label: "Last 30 days" },
  { value: "Custom", label: "Custom range" },
];

const Page = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 100%;
  background: #fff;
  color: #27272a;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 12px 24px;
  border-bottom: 1px solid #e4e4e7;
  background: #fafafa;
  h1 { margin: 0; font-size: 16px; font-weight: 600; }
`;

const Heading = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border: 1px solid #e4e4e7;
  border-radius: 6px;
  background: #fff;
  color: #52525b;
  cursor: pointer;
  &:hover:not(:disabled) { background: #f4f4f5; }
  &:disabled { opacity: 0.4; cursor: default; }
  &:focus-visible { outline: 2px solid #0f766e; outline-offset: 2px; }
`;

const BackLink = IconButton.withComponent(Link);

const Content = styled.main`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  @media (max-width: 600px) { padding: 16px; }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
`;

const SearchField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 280px;
  max-width: 100%;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d4d4d8;
  border-radius: 6px;
  color: #71717a;
  &:focus-within { outline: 2px solid #0f766e; outline-offset: 1px; }
  input {
    width: 100%;
    min-width: 0;
    border: 0;
    outline: none;
    background: transparent;
    color: #27272a;
    font-size: 14px;
  }
`;

const FilterFields = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1 1 640px;
`;

const FilterSelect = styled.select`
  width: 180px;
  max-width: 100%;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d4d4d8;
  border-radius: 6px;
  background: #fff;
  color: #27272a;
  font-size: 13px;
  &:focus-visible { outline: 2px solid #0f766e; outline-offset: 1px; }
`;

const SortControl = styled.div`
  display: inline-flex;
  flex-shrink: 0;
  padding: 3px;
  gap: 3px;
  border: 1px solid #e4e4e7;
  border-radius: 6px;
  background: #f4f4f5;
  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 5px 12px;
    border: 1px solid transparent;
    border-radius: 4px;
    color: #52525b;
    font-size: 13px;
    cursor: pointer;
  }
  button[aria-pressed="true"] { border-color: #d4d4d8; background: #fff; color: #18181b; }
  button:focus-visible { outline: 2px solid #0f766e; outline-offset: 1px; }
`;

const SortTools = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TableViewport = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const HistoryTable = styled.table`
  width: 100%;
  min-width: 760px;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  text-align: left;
  font-size: 13px;
  th {
    padding: 10px 16px;
    border-bottom: 1px solid #d4d4d8;
    color: #71717a;
    font-size: 12px;
    font-weight: 600;
  }
  td { height: 48px; padding: 10px 16px; border-bottom: 1px solid #ededf0; overflow-wrap: anywhere; }
  td:first-of-type { white-space: nowrap; font-variant-numeric: tabular-nums; }
  a { color: #0369a1; font-weight: 600; text-decoration: none; }
  a:hover { text-decoration: underline; }
`;

const HistoryRow = styled.tr<{ shaded: boolean; groupStart: boolean; groupEnd: boolean }>`
  background: ${({ shaded }) => shaded ? "#f4f6f8" : "transparent"};
  ${({ groupStart }) => groupStart && "& > td { border-top: 10px solid #fff; }"}
  ${({ groupEnd }) => groupEnd && "& > td { border-bottom: 10px solid #fff; }"}
  & > td {
    box-shadow: inset 0 ${({ groupStart }) => groupStart ? 1 : 0}px 0 #d4d4d8,
      inset 0 ${({ groupEnd }) => groupEnd ? -1 : 0}px 0 #d4d4d8;
  }
`;

const EVENT_COLORS: Record<QueueHistoryEventType, string> = {
  Claimed: "#0369a1",
  Actioned: "#047857",
  TakenOver: "#a21caf",
  Abandoned: "#be123c",
  Unclaimed: "#a16207",
};

const EventLabel = styled.span<{ eventType: QueueHistoryEventType }>`
  color: ${({ eventType }) => EVENT_COLORS[eventType]};
  font-weight: 600;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  color: #71717a;
  font-size: 12px;
`;

const PageControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StateMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 140px;
  padding: 24px;
  color: #71717a;
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  margin-bottom: 16px;
  padding: 12px 16px;
  border-left: 3px solid #be123c;
  background: #fff1f2;
  color: #9f1239;
  font-size: 14px;
`;

const RangeWarning = styled.div`
  margin-bottom: 16px;
  padding: 12px 16px;
  border-left: 3px solid #a16207;
  background: #fefce8;
  color: #854d0e;
  font-size: 14px;
`;

export function UnderwritingQueueAuditLog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [periodPopoverOpen, setPeriodPopoverOpen] = useState(false);
  const [showCustomCalendar, setShowCustomCalendar] = useState(false);
  const [draftCustomRange, setDraftCustomRange] = useState<DateRange | undefined>();
  const customStart = searchParams.get("from");
  const customEnd = searchParams.get("to");
  const parsedStart = customStart ? parseISO(customStart) : null;
  const parsedEnd = customEnd ? parseISO(customEnd) : null;
  const customRange: QueueCustomRange | null = parsedStart && parsedEnd && isValid(parsedStart) && isValid(parsedEnd)
    ? { start: format(parsedStart <= parsedEnd ? parsedStart : parsedEnd, "yyyy-MM-dd"), end: format(parsedStart <= parsedEnd ? parsedEnd : parsedStart, "yyyy-MM-dd") }
    : null;
  const periodParam = searchParams.get("period");
  const period: QueuePeriod = periodParam === "Custom" && customRange ? "Custom"
    : periodParam === "Today" || periodParam === "Last30Days" ? periodParam : "Last7Days";
  const { start: periodStart, end: periodEnd } = computeQueuePeriodRange(period, customRange);
  const periodLabel = period === "Custom" ? `${format(parseISO(periodStart), "dd MMM")} - ${format(parseISO(periodEnd), "dd MMM")}`
    : PERIOD_OPTIONS.find((option) => option.value === period)!.label;
  const exceedsHistoryLimit = period === "Custom" && (
    differenceInCalendarDays(parseISO(periodEnd), parseISO(periodStart)) >= 60
    || parseISO(periodStart) < subDays(new Date(), 60)
  );
  const historyResult = useApi((signal) => underwritingService.getQueueHistory(signal));
  const sort: QueueHistorySort = searchParams.get("sort") === "date" ? "date" : "application";
  const direction: QueueHistoryDirection = searchParams.get("order") === "oldest" ? "oldest" : "latest";
  const applicationSearch = searchParams.get("app") ?? "";
  const agentParam = searchParams.get("agent");
  const agentId = agentParam && Number.isSafeInteger(Number(agentParam)) && Number(agentParam) > 0 ? Number(agentParam) : undefined;
  const actionParam = searchParams.get("action");
  const eventType = actionParam && Object.prototype.hasOwnProperty.call(QUEUE_HISTORY_EVENT_LABELS, actionParam) ? actionParam as QueueHistoryEventType : undefined;
  const agents = Array.from(new Map((historyResult.data ?? []).map((claim) => [claim.agentId, claim.claimedBy])).entries())
    .sort((first, second) => first[1].localeCompare(second[1]) || first[0] - second[0]);
  const rows = getQueueHistoryRows(historyResult.data ?? [], sort, applicationSearch, direction, {
    agentId, eventType, start: `${periodStart}T00:00:00`, end: `${periodEnd}T23:59:59.999`,
  });
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const requestedPage = Number(searchParams.get("page") ?? 1);
  const page = Number.isSafeInteger(requestedPage) ? Math.min(pageCount, Math.max(1, requestedPage)) : 1;
  const startIndex = (page - 1) * PAGE_SIZE;
  const visibleRows = rows.slice(startIndex, startIndex + PAGE_SIZE);

  const handlePeriodChange = (nextPeriod: QueuePeriod) => {
    if (nextPeriod === "Custom") {
      setDraftCustomRange(customRange ? { from: parseISO(customRange.start), to: parseISO(customRange.end) } : undefined);
      setShowCustomCalendar(true);
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("period", nextPeriod);
    next.delete("from");
    next.delete("to");
    next.delete("page");
    setSearchParams(next, { replace: true });
    setShowCustomCalendar(false);
    setPeriodPopoverOpen(false);
  };

  const handleCustomApply = () => {
    if (!draftCustomRange?.from || !draftCustomRange.to) return;
    const from = draftCustomRange.from <= draftCustomRange.to ? draftCustomRange.from : draftCustomRange.to;
    const to = draftCustomRange.from <= draftCustomRange.to ? draftCustomRange.to : draftCustomRange.from;
    const next = new URLSearchParams(searchParams);
    next.set("period", "Custom");
    next.set("from", format(from, "yyyy-MM-dd"));
    next.set("to", format(to, "yyyy-MM-dd"));
    next.delete("page");
    setSearchParams(next, { replace: true });
    setShowCustomCalendar(false);
    setPeriodPopoverOpen(false);
  };

  const handleSearch = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("app", value);
    else next.delete("app");
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const handleSort = (value: QueueHistorySort) => {
    const next = new URLSearchParams(searchParams);
    next.set("sort", value);
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const handleFilterChange = (filter: "agent" | "action", value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(filter, value);
    else next.delete(filter);
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const handleSortDirection = () => {
    const next = new URLSearchParams(searchParams);
    next.set("order", direction === "latest" ? "oldest" : "latest");
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const handlePageChange = (value: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(value));
    setSearchParams(next, { replace: true });
  };

  return (
    <Page>
      <Header>
        <Heading>
          <BackLink to="/dealer-direct-prop-queue" aria-label="Back to queue" title="Back to queue">
            <ArrowLeft size={16} />
          </BackLink>
          <div>
            <h1>Audit log</h1>
            <span className="text-xs text-muted-foreground">Dealer Direct Prop Queue</span>
          </div>
        </Heading>
        <div className="flex items-center gap-2">
          <Popover open={periodPopoverOpen} onOpenChange={(open) => { setPeriodPopoverOpen(open); if (!open) setShowCustomCalendar(false); }}>
            <PopoverTrigger asChild>
              <button aria-label={`Reporting period: ${periodLabel}`} className="flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:bg-muted">
                <CalendarDays className="size-3.5 text-muted-foreground" />
                <span className="font-medium">{periodLabel}</span>
                <ChevronDown className="size-3.5 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-0">
              {!showCustomCalendar ? (
                <div className="w-40 p-1">
                  {PERIOD_OPTIONS.map((option) => (
                    <button key={option.value} onClick={() => handlePeriodChange(option.value)} className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted">
                      <span className={period === option.value ? "font-semibold" : ""}>{option.label}</span>
                      {period === option.value && <Check className="size-4" />}
                    </button>
                  ))}
                </div>
              ) : (
                <>
                  <div className="border-b px-3 py-2 text-sm font-medium">Select date range</div>
                  <Calendar mode="range" selected={draftCustomRange} onSelect={setDraftCustomRange} numberOfMonths={1} captionLayout="dropdown" />
                  <div className="flex items-center justify-end gap-2 border-t p-3">
                    <button className="rounded-md border border-border px-3 py-1.5 text-xs hover:bg-muted" onClick={() => { setShowCustomCalendar(false); setPeriodPopoverOpen(false); }}>Cancel</button>
                    <button className="rounded-md bg-slate-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-700 disabled:opacity-50" disabled={!draftCustomRange?.from || !draftCustomRange.to} onClick={handleCustomApply}>Apply</button>
                  </div>
                </>
              )}
            </PopoverContent>
          </Popover>
          <IconButton onClick={historyResult.refetch} disabled={historyResult.loading} aria-label="Refresh audit log" title="Refresh audit log">
            <RefreshCw size={16} className={historyResult.loading ? "animate-spin" : ""} />
          </IconButton>
        </div>
      </Header>
      <Content>
        <Toolbar>
          <FilterFields>
            <SearchField>
              <Search size={16} aria-hidden="true" />
              <input type="search" inputMode="numeric" aria-label="Search application ID" placeholder="Search application ID" value={applicationSearch} onChange={(event) => handleSearch(event.target.value)} />
            </SearchField>
            <FilterSelect aria-label="Filter by agent" value={agentId === undefined ? "" : String(agentId)} onChange={(event) => handleFilterChange("agent", event.target.value)}>
              <option value="">All agents</option>
              {agentId !== undefined && !agents.some(([id]) => id === agentId) && <option value={agentId}>Agent {agentId}</option>}
              {agents.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </FilterSelect>
            <FilterSelect aria-label="Filter by action" value={eventType ?? ""} onChange={(event) => handleFilterChange("action", event.target.value)}>
              <option value="">All actions</option>
              {Object.entries(QUEUE_HISTORY_EVENT_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </FilterSelect>
          </FilterFields>
          <SortTools>
            <SortControl role="group" aria-label="Sort history">
              <button type="button" aria-pressed={sort === "date"} onClick={() => handleSort("date")}>
                <CalendarDays size={14} />Date
              </button>
              <button type="button" aria-pressed={sort === "application"} onClick={() => handleSort("application")}>
                <ListTree size={14} />Application
              </button>
            </SortControl>
            <IconButton
              aria-label={direction === "latest" ? "Show oldest first" : "Show latest first"}
              title={direction === "latest" ? "Latest first: show oldest first" : "Oldest first: show latest first"}
              aria-pressed={direction === "oldest"}
              onClick={handleSortDirection}
            >
              <ArrowUpDown size={16} />
            </IconButton>
          </SortTools>
        </Toolbar>
        {exceedsHistoryLimit && <RangeWarning role="alert">History is limited to a maximum of the last 60 days. Older dates in this selection will not be included.</RangeWarning>}
        {historyResult.error && <ErrorMessage role="alert">{historyResult.error}</ErrorMessage>}
        {historyResult.loading && !historyResult.data ? (
          <StateMessage role="status" aria-label="Loading audit log"><Spinner /></StateMessage>
        ) : !historyResult.error && rows.length === 0 ? (
          <StateMessage>{agentId !== undefined || eventType ? "No history matches these filters." : applicationSearch.trim() ? "No history matches this application ID." : "No activity in the selected period."}</StateMessage>
        ) : rows.length > 0 ? (
          <>
            <TableViewport>
              <HistoryTable aria-label="Queue audit history" aria-busy={historyResult.loading}>
                <colgroup>
                  <col style={{ width: "30%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "30%" }} />
                </colgroup>
                <thead><tr><th scope="col">Date and time</th><th scope="col">Application</th><th scope="col">Action</th><th scope="col">Claim holder</th></tr></thead>
                <tbody>
                  {visibleRows.map((row, rowIndex) => (
                    <HistoryRow
                      key={row.id}
                      shaded={row.shaded}
                      groupStart={sort === "application" && (rowIndex === 0 || row.applicationId !== visibleRows[rowIndex - 1].applicationId)}
                      groupEnd={sort === "application" && (rowIndex === visibleRows.length - 1 || row.applicationId !== visibleRows[rowIndex + 1].applicationId)}
                    >
                      <td><time dateTime={row.occurredOn}>{format(parseISO(row.occurredOn), "dd MMM yyyy, HH:mm:ss")}</time></td>
                      <td>
                        <Link to={`/dealer-direct-prop-queue/${row.applicationId}`} state={{ origin: { path: `/dealer-direct-prop-queue/audit-log?${searchParams}`, label: "Audit log" } }}>{row.applicationId}</Link>
                      </td>
                      <td><EventLabel eventType={row.eventType}>{QUEUE_HISTORY_EVENT_LABELS[row.eventType]}</EventLabel></td>
                      <td>{row.claimedBy}</td>
                    </HistoryRow>
                  ))}
                </tbody>
              </HistoryTable>
            </TableViewport>
            <Pagination>
              <span>{startIndex + 1}-{Math.min(startIndex + PAGE_SIZE, rows.length)} of {rows.length} events</span>
              <PageControls>
                <IconButton aria-label="Previous page" title="Previous page" disabled={page === 1} onClick={() => handlePageChange(page - 1)}><ChevronLeft size={16} /></IconButton>
                <span>{page} / {pageCount}</span>
                <IconButton aria-label="Next page" title="Next page" disabled={page === pageCount} onClick={() => handlePageChange(page + 1)}><ChevronRight size={16} /></IconButton>
              </PageControls>
            </Pagination>
          </>
        ) : null}
      </Content>
    </Page>
  );
}